// The one view of desktop and phone: a zoom with visibility layers, and the
// phone's tilt-to-map switch. Pure state machines (no three, no DOM), so
// scripts/check-view.mjs runs them in node. APP_PLAYER.md §5.
//
// Zoom model (vertical FOVs throughout):
//   fov0  = desktopVFov(aspect)          the naked view at 1×
//   Z     = fov0 / fov                    the one zoom number (MAG readout)
//   Zmax  = fov0 / (bandFov / maxZoom)    ≈ 35 at 16:9 (10° / 6 = 1.67° floor)
//   band  = fov <= bandFov                the optic band: main.js renders through
//                                         weapons.monoCam with every optic rule
//                                         (dehaze, clarity, focus wedge, depth)
//   monoZoom = bandFov / fov ∈ [1, maxZoom] (the optic's own zoom, label rules)
//   w     = ln Z / ln(fov0 / bandFov) ∈ [0, 1]  the HUD morph (0 = 1×, 1 = band)
// Wheel, pinch and the phone slider all move ln Z / ln Zmax.

export const REF_ASPECT = 16 / 9;
const DEG = Math.PI / 180;
const REF_HFOV = 90 * DEG;            // design hFOV at 16:9
const MAX_HFOV = 110 * DEG;           // perceptual cap
const REF_VFOV = 2 * Math.atan(Math.tan(REF_HFOV / 2) / REF_ASPECT);   // ≈ 58.7°

// Hor+ / Vert+ vertical FOV (degrees) for a viewport aspect: 90° hFOV at
// 16:9; narrower screens keep the hFOV and grow the vFOV; wider ones keep the
// vFOV until the hFOV hits 110°, then lock the hFOV.
export function desktopVFov(aspect) {
  if (aspect <= REF_ASPECT) return 2 * Math.atan(Math.tan(REF_HFOV / 2) / aspect) / DEG;
  const hFov = 2 * Math.atan(Math.tan(REF_VFOV / 2) * aspect);
  if (hFov <= MAX_HFOV) return REF_VFOV / DEG;
  return 2 * Math.atan(Math.tan(MAX_HFOV / 2) / aspect) / DEG;
}

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const BAND_EPS = 1e-6;                // fov === bandFov is IN the band

export class ViewZoom {
  // bandFov = tunables.weapons.monocular.fov_deg, maxZoom = .max_zoom,
  // step = tunables.view.wheel_step (one wheel notch), rampNotches = notches
  // into the band over which the optic's dehaze + clarity fade in
  constructor({ aspect = REF_ASPECT, bandFov = 10, maxZoom = 6, step = 1.12, rampNotches = 2, rate = 12 } = {}) {
    this.bandFov = bandFov;
    this.maxZoom = maxZoom;
    this.step = step;
    this.rampNotches = rampNotches;
    this.rate = rate;                 // log-space easing rate (1/s)
    this.target = 1;                  // Z the input asks for
    this.z = 1;                       // Z on screen (eased toward target)
    this.armed = false;               // focusArm state (hysteresis)
    this.setAspect(aspect);
  }

  configure({ bandFov, maxZoom, step } = {}) {
    if (bandFov > 0) this.bandFov = bandFov;
    if (maxZoom >= 1) this.maxZoom = maxZoom;
    if (step > 1) this.step = step;
    this.setAspect(this.aspect);
  }

  // a resize keeps Z (the magnification the player chose), clamped to the
  // new range
  setAspect(aspect) {
    this.aspect = aspect > 0 ? aspect : REF_ASPECT;
    this.fov0 = desktopVFov(this.aspect);
    this.target = this._clamp(this.target);
    this.z = this._clamp(this.z);
  }

  get zMax() { return Math.max(1, this.fov0 / (this.bandFov / this.maxZoom)); }
  get zBand() { return Math.max(1, this.fov0 / this.bandFov); }    // Z at band entry
  _clamp(z) { return Math.max(1, Math.min(this.zMax, z)); }

  get fov() { return this.fov0 / this.z; }
  get band() { return this.fov <= this.bandFov + BAND_EPS; }
  // the optic's zoom inside the band (1 at entry, maxZoom at Zmax); 1 outside
  get monoZoom() { return Math.max(1, Math.min(this.maxZoom, this.bandFov / this.fov)); }
  // HUD morph: 0 at 1×, 1 from band entry on
  get w() {
    const lb = Math.log(this.zBand);
    return lb > 0 ? clamp01(Math.log(this.z) / lb) : 1;
  }
  // optic dehaze + clarity strength: 0 at band entry → 1 after rampNotches
  // wheel notches into the band (no pop when the render path changes)
  get clearRamp() {
    if (!this.band) return 0;
    const span = this.rampNotches * Math.log(this.step);
    return span > 0 ? clamp01(Math.log(this.z / this.zBand) / span) : 1;
  }
  // look sensitivity factor (mouse / drag radians per px): fov / fov0
  get lookScale() { return 1 / this.z; }

  // slider / MAG position: ln Z / ln Zmax of the TARGET (input feedback is
  // immediate, the picture eases)
  get frac() {
    const lm = Math.log(this.zMax);
    return lm > 0 ? clamp01(Math.log(this.target) / lm) : 0;
  }
  setFrac(f) { this.target = this._clamp(Math.exp(clamp01(f) * Math.log(this.zMax))); }
  setZ(z) { this.target = this._clamp(z); }

  // one wheel notch per call: deltaY < 0 (scroll up) zooms in
  wheel(deltaY) {
    if (!deltaY) return;
    this.target = this._clamp(this.target * (deltaY < 0 ? this.step : 1 / this.step));
  }
  // pinch: scale = current finger distance / previous (> 1 spreads = zoom in)
  pinch(scale) {
    if (!(scale > 0)) return;
    this.target = this._clamp(this.target * scale);
  }
  reset() { this.target = 1; }
  snap() { this.z = this.target; }

  // the focus wedge's VIEW arm (renderconfig.focus.view_arm, focus.js view
  // mode): on once the shown Z reaches zOn, off again only below zOn / hyst
  // (one wheel notch at 1.12) — so a zoom resting on the threshold never
  // flips the terrain want-set. The state is kept through the band; main.js
  // uses it below the band only. Call once per frame after update().
  focusArm(zOn, hyst = 1.12) {
    if (!(zOn > 1)) return (this.armed = false);
    const off = zOn / Math.max(1, hyst);
    this.armed = this.armed ? this.z >= off - 1e-9 : this.z >= zOn - 1e-9;
    return this.armed;
  }

  // ease the shown Z toward the target in log space (equal speed per ratio)
  update(dt) {
    const lt = Math.log(this.target), lz = Math.log(this.z);
    const k = Math.min(1, this.rate * Math.max(0, dt));
    const nz = lz + (lt - lz) * k;
    this.z = Math.abs(lt - nz) < 1e-4 ? this.target : Math.exp(nz);
    return this;
  }
}

// Phone: tip the phone flat (camera forward pitched down past enterDeg,
// held dwellMs) and the Map opens heading-up; raise it past exitDeg and it
// closes. Only a map the tilt opened is closed by the tilt. After the map
// was closed any other way while still tipped down, the switch re-arms only
// once the phone came back up past exitDeg. `faceUp` = the screen faces the
// sky (lying on your back with the phone overhead never triggers). `locked`
// (the 🔒 button) freezes the switch with the view.
export class TiltSwitch {
  constructor({ enterDeg = 65, exitDeg = 50, dwellMs = 300 } = {}) {
    this.configure({ enterDeg, exitDeg, dwellMs });
    this.open = false;        // a map the tilt opened is showing
    this.armed = true;
    this.locked = false;
    this._since = null;       // when the enter condition started holding
  }

  configure({ enterDeg, exitDeg, dwellMs } = {}) {
    if (enterDeg > 0) this.enterDeg = enterDeg;
    if (exitDeg > 0) this.exitDeg = exitDeg;
    if (dwellMs >= 0) this.dwellMs = dwellMs;
    if (this.exitDeg > this.enterDeg) this.exitDeg = this.enterDeg;   // keep the hysteresis
  }

  // the map was closed (or opened) by something else: forget the tilt's claim
  release() {
    this.open = false;
    this.armed = false;
    this._since = null;
  }

  // pitchDeg = camera forward pitch (negative = down). → 'open' | 'close' | null
  update(pitchDeg, faceUp, now) {
    if (this.locked || !Number.isFinite(pitchDeg)) { this._since = null; return null; }
    const up = pitchDeg >= -this.exitDeg;
    if (this.open) {
      if (up) { this.open = false; this.armed = true; this._since = null; return 'close'; }
      return null;
    }
    if (up) this.armed = true;
    const down = faceUp && pitchDeg <= -this.enterDeg;
    if (!down || !this.armed) { this._since = null; return null; }
    if (this._since === null) this._since = now;
    if (now - this._since >= this.dwellMs) {
      this.open = true;
      this.armed = false;
      this._since = null;
      return 'open';
    }
    return null;
  }
}
